/**
 * CadenceQueue — /cadence/bdr and /cadence/ae
 *
 * Daily execution view. BDR / AE walks in, sees grouped queue by step,
 * one-click logs each action. No double-entry into activity log.
 *
 * Palette: Mi6 black / red / soft off-white. No per-role accents.
 */

import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { useLocation } from "wouter";
import SecondaryNav, { type SecondaryNavRole } from "@/components/dashboards/SecondaryNav";
import AddPhoneInline from "@/components/dial-and-log/AddPhoneInline";
import {
  CADENCE_PROGRAM,
  type CadenceProspect,
  type CadenceStepId,
  type CadenceActionKind,
  type ProspectSource,
  loadProspects,
  loadActions,
  saveProspects,
  completeStep,
  setProspectStatus,
  enrollProspect,
  seedDemoProspectsIfEmpty,
  getDailyQueue,
} from "@/lib/cadence-store";
import ComposeModal, { type ComposeChannel } from "@/components/cadence/ComposeModal";
import { api, getCurrentTenant, getSession } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";

const MI6_BLACK = "#0B0B0F";
const MI6_RED = "#DC2626";

// ─── Map cadence action kind to compose channel ───────────────────────────────

function actionKindToChannel(kind: CadenceActionKind): ComposeChannel {
  switch (kind) {
    case "email_opening": return "email_opening";
    case "email_followup": return "email_followup";
    case "linkedin_dm": return "linkedin_dm";
    case "phone_no_vm": return "phone_no_vm";
    case "phone_with_vm": return "phone_with_vm";
    case "decision_gate": return "phone_no_vm"; // fallback — gate handled separately
    default: return "phone_no_vm";
  }
}

// ─── Source badges ────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<ProspectSource, string> = {
  marketing_mql: "Marketing MQL",
  cold_outbound: "Cold",
  event: "Event",
  referral: "Referral",
  manager_assigned: "Manager",
};

function SourceBadge({ source }: { source: ProspectSource }) {
  return (
    <span
      className="inline-flex items-center text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded"
      style={{
        background: "#EFEFEE",
        color: "#525258",
        border: "1px solid #E5E5E7",
      }}
    >
      {SOURCE_LABELS[source]}
    </span>
  );
}

// ─── Queue tab type ──────────────────────────────────────────────────────────
// Tab 1: Cold cadence  — status='lead' or no prior contact attempts
// Tab 2: Active follow-ups — status='prospect' and at least 1 activity logged
// (localStorage-based until activity_log migration is applied)

type QueueTab = "cold" | "active";

// ─── Step icon helper ─────────────────────────────────────────────────────────

function stepIcon(stepId: CadenceStepId): string {
  switch (stepId) {
    case 1: return "Em";
    case 2: return "Ph";
    case 3: return "in";
    case 4: return "Ph";
    case 5: return "Em";
    case 6: return "Ph";
    case 7: return "◇";
    default: return "·";
  }
}

// ─── Per-prospect row ─────────────────────────────────────────────────────────

function ProspectRow({
  prospect,
  isOverdue,
  onComplete,
  onSkipToGate,
  stepKind,
  stepLabel,
}: {
  prospect: CadenceProspect;
  isOverdue: boolean;
  onComplete: (id: string, step: CadenceStepId) => void;
  onSkipToGate?: () => void;
  stepKind?: CadenceActionKind;
  stepLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [localPhone, setLocalPhone] = useState(prospect.phone);

  const channel: ComposeChannel = stepKind
    ? actionKindToChannel(stepKind)
    : "email_opening";

  return (
    <div
      className="bg-white border border-neutral-200 rounded-md transition-all hover:border-neutral-300"
      style={isOverdue ? { borderLeft: `3px solid ${MI6_RED}` } : { borderLeft: `3px solid #E5E5E7` }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="font-semibold text-sm" style={{ color: MI6_BLACK }}>
              {prospect.full_name}
            </span>
            {prospect.title && (
              <>
                <span className="text-xs text-neutral-500">·</span>
                <span className="text-xs text-neutral-600">{prospect.title}</span>
              </>
            )}
            {prospect.company && (
              <>
                <span className="text-xs text-neutral-500">@</span>
                <span className="text-xs font-medium text-neutral-700">{prospect.company}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            <SourceBadge source={prospect.source} />
            {isOverdue && (
              <span
                className="text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded text-white"
                style={{ background: MI6_RED }}
              >
                Overdue
              </span>
            )}
          </div>
          {prospect.last_action_at && (
            <div className="text-[10px] text-neutral-500 mt-0.5">
              Last touch: {new Date(prospect.last_action_at).toLocaleDateString()}
            </div>
          )}
          {prospect.notes && (
            <div className="text-[11px] text-neutral-500 italic mt-1">{prospect.notes}</div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => setOpen(!open)}
            className="text-[10px] uppercase tracking-wider text-neutral-500 hover:text-neutral-600 transition-colors"
          >
            {open ? "less" : "more"}
          </button>
          {prospect.current_step !== 7 ? (
            <>
              <button
                onClick={() => setComposeOpen(true)}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all hover:shadow-md active:scale-95"
                style={{ background: MI6_RED }}
              >
                Compose
              </button>
              <button
                onClick={() => onComplete(prospect.id, prospect.current_step)}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all hover:shadow-md active:scale-95"
                style={{ background: MI6_BLACK }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#374151")}
                onMouseLeave={(e) => (e.currentTarget.style.background = MI6_BLACK)}
              >
                Mark Done
              </button>
            </>
          ) : (
            <button
              onClick={onSkipToGate}
              className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white shadow-sm"
              style={{ background: MI6_RED }}
            >
              Decide
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="px-3 pb-3 pt-0 text-[11px] text-neutral-600 grid grid-cols-1 sm:grid-cols-3 gap-2 border-t border-neutral-100">
          {prospect.email && (
            <div>
              <span className="text-neutral-500 uppercase tracking-wider text-[9px]">Email</span>
              <div>{prospect.email}</div>
            </div>
          )}
          <div>
            <span className="text-neutral-500 uppercase tracking-wider text-[9px]">Phone</span>
            {localPhone ? (
              <div>{localPhone}</div>
            ) : (
              <AddPhoneInline
                contactId={null /* wire to real decision_makers.id when Supabase row is linked */}
                onSaved={(phone) => setLocalPhone(phone)}
              />
            )}
          </div>
          {prospect.linkedin && (
            <div>
              <span className="text-neutral-500 uppercase tracking-wider text-[9px]">LinkedIn</span>
              <div>{prospect.linkedin}</div>
            </div>
          )}
        </div>
      )}

      {/* Compose modal — only mounted when open to keep DOM clean */}
      <ComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        channel={channel}
        stepLabel={stepLabel}
        prospect={{
          full_name: prospect.full_name,
          first_name: prospect.full_name.split(" ")[0],
          title: prospect.title,
          company: prospect.company,
          email: prospect.email,
          phone: prospect.phone,
          linkedin: prospect.linkedin,
        }}
        onSent={() => {
          onComplete(prospect.id, prospect.current_step);
          setComposeOpen(false);
        }}
      />
    </div>
  );
}

// ─── Pursuit Decision panel ──────────────────────────────────────────────────────

function DecisionGateRow({
  prospect,
  onResolve,
}: {
  prospect: CadenceProspect;
  onResolve: () => void;
}) {
  return (
    <div
      className="bg-white border rounded-md p-3"
      style={{ borderLeft: `3px solid ${MI6_RED}` }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm" style={{ color: MI6_BLACK }}>
              {prospect.full_name}
            </span>
            <span className="text-xs text-neutral-500">·</span>
            <span className="text-xs text-neutral-600">{prospect.title}</span>
            <span className="text-xs text-neutral-500">@</span>
            <span className="text-xs font-medium text-neutral-700">{prospect.company}</span>
            <SourceBadge source={prospect.source} />
          </div>
          {prospect.notes && (
            <div className="text-[11px] text-neutral-500 italic mt-1">{prospect.notes}</div>
          )}
          <div className="text-[10px] text-neutral-500 mt-1">
            Cadence complete · enrolled {new Date(prospect.enrolled_at).toLocaleDateString()}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setProspectStatus(prospect.id, "marketing_hook", "TBD");
              onResolve();
            }}
            className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white"
            style={{ background: MI6_RED }}
          >
            Marketing Hook
          </button>
          <button
            onClick={() => {
              setProspectStatus(prospect.id, "paused");
              onResolve();
            }}
            className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider"
            style={{ background: "#EFEFEE", color: MI6_BLACK, border: "1px solid #C9C9CC" }}
          >
            Park 90 Days
          </button>
          <button
            onClick={() => {
              setProspectStatus(prospect.id, "disqualified");
              onResolve();
            }}
            className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-neutral-500"
            style={{ background: "white", border: "1px solid #C9C9CC" }}
          >
            Disqualify
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Completion counter color ──────────────────────────────────────────────────

function completionColor(pct: number): string {
  if (pct >= 100) return "#16A34A"; // green-600
  if (pct >= 70)  return MI6_BLACK;
  return MI6_RED;
}

// ─── Cadence Step group section ───────────────────────────────────────────────

function StepGroup({
  step,
  prospects,
  today,
  onComplete,
  onResolve,
  doneCount,
}: {
  step: typeof CADENCE_PROGRAM[number];
  prospects: CadenceProspect[];
  today: string;
  onComplete: (id: string, step: CadenceStepId) => void;
  onResolve: () => void;
  doneCount: number;
}) {
  const [collapsed, setCollapsed] = useState(false);
  if (prospects.length === 0) return null;

  const total = prospects.length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const color = completionColor(pct);

  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full text-left flex items-center justify-between gap-3 px-5 py-3 border-b border-neutral-100 hover:bg-neutral-50 transition-colors"
        style={{ borderLeft: `4px solid ${step.id === 7 ? MI6_RED : MI6_BLACK}` }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-sm"
            style={{ background: step.id === 7 ? MI6_RED : MI6_BLACK }}
          >
            {step.id}
          </div>
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.22em] font-bold whitespace-nowrap"
              style={{ color: step.id === 7 ? MI6_RED : MI6_BLACK }}
            >
              Cadence Step {step.id} · Day {step.day_offset}{step.id === 7 ? "+" : ""}
            </div>
            <div className="text-sm font-bold" style={{ color: MI6_BLACK }}>
              {step.short}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="text-xs font-bold tabular-nums px-2 py-0.5 rounded"
            style={{ background: "#EFEFEE", color }}
          >
            {doneCount} of {total} · {pct}%
          </span>
          <span className="text-neutral-500 text-xs">{collapsed ? "▾" : "▴"}</span>
        </div>
      </button>
      {!collapsed && (
        <div className="px-3 py-3 space-y-2">
          {step.script_hint && (
            <div className="text-[11px] text-neutral-500 italic px-1 mb-1">
              {step.script_hint}
            </div>
          )}
          {step.id === 7
            ? prospects.map((p) => (
                <DecisionGateRow key={p.id} prospect={p} onResolve={onResolve} />
              ))
            : prospects.map((p) => (
                <ProspectRow
                  key={p.id}
                  prospect={p}
                  isOverdue={p.next_action_at < today}
                  onComplete={onComplete}
                  stepKind={step.kind}
                  stepLabel={`Step ${step.id}`}
                />
              ))}
        </div>
      )}
    </div>
  );
}

// ─── Add prospect modal ───────────────────────────────────────────────────────

function AddProspectModal({
  open,
  onClose,
  onAdded,
  ownerId,
  ownerRole,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  ownerId: string;
  ownerRole: "bdr" | "ae";
}) {
  const [fullName, setFullName] = useState("");
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [source, setSource] = useState<ProspectSource>("cold_outbound");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // Esc key dismissal
  useEffect(() => {
    if (!open) return;
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape" && !saving) onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, saving, onClose]);

  if (!open) return null;

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!fullName.trim()) errs.full_name = "Full name is required";
    if (!company.trim()) errs.company = "Company is required";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      // Persist to Supabase via server API
      const saved = await api<{ id: number; company: string; full_name: string }>("/api/prospects", {
        method: "POST",
        body: JSON.stringify({
          full_name: fullName.trim(),
          title: title.trim(),
          company: company.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          linkedin: linkedin.trim() || undefined,
          source,
          owner_role: ownerRole,
          notes: notes.trim() || undefined,
        }),
      });

      // Also enroll in localStorage so the cadence queue renders immediately
      enrollProspect({
        owner_id: ownerId,
        owner_role: ownerRole,
        full_name: fullName.trim(),
        title: title.trim(),
        company: company.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        linkedin: linkedin.trim() || undefined,
        source,
        notes: notes.trim() || undefined,
      });

      toast({
        title: "Prospect saved",
        description: `${saved.company} enrolled at Cadence Step 1 and saved to your account.`,
      });

      // Reset form
      setFullName("");
      setTitle("");
      setCompany("");
      setEmail("");
      setPhone("");
      setLinkedin("");
      setNotes("");
      setFieldErrors({});

      onAdded();
      onClose();

      // Navigate to BDR Discovery (initial questions / CP1) with company pre-filled
      const encoded = encodeURIComponent(saved.company);
      navigate(`/bdr?account=${encoded}`);
    } catch (e: any) {
      console.error("[AddProspect] save failed:", e?.message);
      toast({
        title: "Save failed",
        description: e?.message ?? "Could not save prospect. Check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 pt-5 pb-3 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: MI6_BLACK }}>
              Add Prospect
            </div>
            <div className="text-sm text-neutral-500">Enrolls at Cadence Step 1 · Day 0</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
            aria-label="Close"
            disabled={saving}
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-600 mb-1">
              Full Name <span style={{ color: MI6_RED }}>*</span>
            </label>
            <input
              value={fullName}
              onChange={(e) => { setFullName(e.target.value); setFieldErrors((p) => ({ ...p, full_name: "" })); }}
              className={`w-full text-sm rounded-md border px-2.5 py-2 focus:outline-none focus:border-neutral-400 ${fieldErrors.full_name ? "border-red-400" : "border-neutral-200"}`}
              autoFocus
            />
            {fieldErrors.full_name && <p className="text-[11px] mt-0.5" style={{ color: MI6_RED }}>{fieldErrors.full_name}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-600 mb-1">
                Title
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full text-sm rounded-md border border-neutral-200 px-2.5 py-2 focus:outline-none focus:border-neutral-400"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-600 mb-1">
                Company <span style={{ color: MI6_RED }}>*</span>
              </label>
              <input
                value={company}
                onChange={(e) => { setCompany(e.target.value); setFieldErrors((p) => ({ ...p, company: "" })); }}
                className={`w-full text-sm rounded-md border px-2.5 py-2 focus:outline-none focus:border-neutral-400 ${fieldErrors.company ? "border-red-400" : "border-neutral-200"}`}
              />
              {fieldErrors.company && <p className="text-[11px] mt-0.5" style={{ color: MI6_RED }}>{fieldErrors.company}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-600 mb-1">
                Email
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full text-sm rounded-md border border-neutral-200 px-2.5 py-2 focus:outline-none focus:border-neutral-400"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-600 mb-1">
                Phone
              </label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full text-sm rounded-md border border-neutral-200 px-2.5 py-2 focus:outline-none focus:border-neutral-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-600 mb-1">
              LinkedIn
            </label>
            <input
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
              placeholder="linkedin.com/in/…"
              className="w-full text-sm rounded-md border border-neutral-200 px-2.5 py-2 focus:outline-none focus:border-neutral-400"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-600 mb-1">
              Source
            </label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value as ProspectSource)}
              className="w-full text-sm rounded-md border border-neutral-200 px-2.5 py-2 bg-white focus:outline-none focus:border-neutral-400"
            >
              <option value="marketing_mql">Marketing MQL</option>
              <option value="cold_outbound">Cold Outbound</option>
              <option value="event">Event</option>
              <option value="referral">Referral</option>
              <option value="manager_assigned">Manager Assigned</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-600 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full text-sm rounded-md border border-neutral-200 px-2.5 py-2 focus:outline-none focus:border-neutral-400 resize-none"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-100 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500 disabled:opacity-40">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
            style={{ background: MI6_RED }}
          >
            {saving ? "Saving..." : "Enroll at Cadence Step 1"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CadenceQueue({ role = "bdr", embedded = false }: { role?: "bdr" | "ae"; embedded?: boolean }) {
  // Live Supabase user_id (uuid). Falls back to the legacy literal
  // "current_user" ONLY when no session is hydrated yet — that lets the
  // page render skeleton state before login resolves.
  const session = getSession();
  const ownerId = session?.id ?? "current_user";
  const isLiveOwner = !!session?.id;

  const [tick, setTick] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  // ── Two-tab queue: Cold cadence vs Active follow-ups ────────────────────
  // Scaffold only — wired to cadence-store status field.
  // "active_followups" tab shows prospects where status='prospect' (converted
  // from lead) or where current_step > 1 (has had at least one contact).
  // Full Supabase wiring happens when activity_log migration is applied.
  const [queueTab, setQueueTab] = useState<QueueTab>("cold");
  const { toast } = useToast();

  // Re-render when background hydration from /api/cadence/* finishes. Without
  // this, a fresh login with empty localStorage renders an empty queue and
  // never updates until the user interacts. cadence-store dispatches these
  // events after every server refresh.
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener("aip:cadence-prospects-refreshed", bump);
    window.addEventListener("aip:cadence-actions-refreshed", bump);
    return () => {
      window.removeEventListener("aip:cadence-prospects-refreshed", bump);
      window.removeEventListener("aip:cadence-actions-refreshed", bump);
    };
  }, []);

  useEffect(() => {
    // Sample prospects only seed for demo tenants. Real tenants start empty.
    const tenant = getCurrentTenant();
    if (tenant?.is_demo === true) {
      seedDemoProspectsIfEmpty(ownerId, role);
    }
    setTick((t) => t + 1);

    // ── One-time enrollment_id backfill (qa-fix-f Decision #4 — narrow) ────────────────────────
    // Runs once per browser session. Finds localStorage prospects that have no
    // _enrollment_id AND have a supabase_id (real Supabase integer id) and
    // calls POST /api/cadence/enrollments for each. Prospects without a
    // supabase_id are skipped (NOT NULL FK constraint — Decision #4).
    const sessionFlag = "aip:enrollment-backfill-done";
    const permanentFlag = "aip:enrollment-backfill-complete";
    if (
      sessionStorage.getItem(sessionFlag) === "1" ||
      localStorage.getItem(permanentFlag) === "1"
    ) {
      return;
    }
    sessionStorage.setItem(sessionFlag, "1");

    async function backfillEnrollmentIds() {
      const prospects = loadProspects();
      // NARROW: only backfill prospects that have a real Supabase id
      const needsBackfill = prospects.filter(
        (p) => !(p as any)._enrollment_id &&
               (p as any).supabase_id &&
               p.owner_id === ownerId
      );
      if (needsBackfill.length === 0) {
        localStorage.setItem(permanentFlag, "1");
        return;
      }

      let migratedCount = 0;
      const updated = [...prospects];

      for (const prospect of needsBackfill) {
        try {
          const enrollment = await api<{ id: string }>("/api/cadence/enrollments", {
            method: "POST",
            body: JSON.stringify({
              prospect_id: (prospect as any).supabase_id,  // must be non-null (narrow)
              cadence_id: "default_mi6",
              owner_role: prospect.owner_role ?? "bdr",
              enrolled_at: prospect.enrolled_at ?? new Date().toISOString().slice(0, 10),
              notes: prospect.notes ?? null,
            }),
          });

          if (enrollment?.id) {
            const idx = updated.findIndex((p) => p.id === prospect.id);
            if (idx >= 0) {
              (updated[idx] as any)._enrollment_id = enrollment.id;
              migratedCount++;
            }
          }
        } catch (err: any) {
          console.warn(
            "[CadenceQueue] backfill: enrollment creation failed for prospect",
            prospect.id,
            err?.message
          );
        }
      }

      if (migratedCount > 0) {
        saveProspects(updated);
        setTick((t) => t + 1);
        toast({
          title: `Migrated ${migratedCount} ${migratedCount === 1 ? "entry" : "entries"} to Supabase`,
          description:
            "Existing cadence entries have been linked to the server. Steps will now write to Supabase.",
        });
      }

      localStorage.setItem(permanentFlag, "1");
    }

    backfillEnrollmentIds().catch((e) =>
      console.error("[CadenceQueue] backfill threw:", e?.message)
    );
  }, [role]);

  const queue = useMemo(() => getDailyQueue(ownerId), [tick]);
  const allMine = useMemo(
    () => loadProspects().filter((p) => p.owner_id === ownerId),
    [tick]
  );

  /**
   * For each step group, compute how many prospects have been marked done
   * today at that step. A prospect is "done today at step N" when there is a
   * CadenceAction with executed_at = today whose step matches the group step
   * AND whose prospect_id is in the group's prospect list (i.e. the prospect
   * was at that step when the action fired, which is recorded on the action).
   */
  const doneCounts = useMemo<Record<number, number>>(() => {
    const today = queue.today;
    const todayActions = loadActions().filter(
      (a) => a.owner_id === ownerId && a.executed_at.slice(0, 10) === today
    );
    // Build a set of (step, prospect_id) pairs that have been actioned today
    const doneSet = new Set(todayActions.map((a) => `${a.step}:${a.prospect_id}`));
    const counts: Record<number, number> = {};
    for (const g of queue.groups) {
      counts[g.step] = g.prospects.filter((p) => doneSet.has(`${g.step}:${p.id}`)).length;
    }
    return counts;
  }, [tick, queue]);

  const activeCount = allMine.filter((p) => p.status === "active").length;
  const hookCount = allMine.filter((p) => p.status === "marketing_hook").length;
  const pausedCount = allMine.filter((p) => p.status === "paused").length;

  // ── Approved marketing hooks (from shared localStorage key) ────────────────
  const approvedHooks = useMemo(() => {
    try {
      const raw = localStorage.getItem("aip:approved-hooks");
      return raw ? (JSON.parse(raw) as Array<{
        id: string;
        eventName: string;
        hookKey: string;
        text: string;
        approved_at: string;
      }>) : [];
    } catch {
      return [];
    }
  }, [tick]);

  // ── Tab filter logic ───────────────────────────────────────────────────────────────
  // Cold cadence: prospects at step 1 (first touch) or who've never had contact.
  // Active follow-ups: prospects past step 1, still in active status.
  // NOTE: Once activity_log migration is applied, replace current_step checks
  // with a real activity_log count query.
  const coldProspectIds = useMemo(
    () => new Set(
      allMine
        .filter((p) => p.status === "active" && p.current_step === 1)
        .map((p) => p.id)
    ),
    [allMine]
  );
  const activeFollowUpIds = useMemo(
    () => new Set(
      allMine
        .filter((p) => p.status === "active" && p.current_step > 1)
        .map((p) => p.id)
    ),
    [allMine]
  );

  // Filter the daily queue groups based on selected tab.
  // When a tab is empty the empty-state message changes.
  const filteredGroups = useMemo(() => {
    if (queueTab === "cold") {
      return queue.groups.map((g) => ({
        ...g,
        prospects: g.prospects.filter((p) => coldProspectIds.has(p.id)),
      })).filter((g) => g.prospects.length > 0);
    }
    // active tab
    return queue.groups.map((g) => ({
      ...g,
      prospects: g.prospects.filter((p) => activeFollowUpIds.has(p.id)),
    })).filter((g) => g.prospects.length > 0);
  }, [queueTab, queue.groups, coldProspectIds, activeFollowUpIds]);

  const coldCount        = coldProspectIds.size;
  const followUpCount    = activeFollowUpIds.size;

  function handleComplete(id: string, step: CadenceStepId) {
    completeStep(id, step);
    setTick((t) => t + 1);
  }
  function refresh() {
    setTick((t) => t + 1);
  }

  // ── Queue content (shared between standalone and embedded modes) ─────────
  const queueContent = (
    <>
      {/* ── Approved marketing hooks panel ──────────────────────────────────── */}
      {approvedHooks.length > 0 && (
        <div
          className="rounded-xl border overflow-hidden"
          style={{ borderColor: "#059669", borderLeft: "4px solid #059669" }}
        >
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: "#F0FDF4" }}>
            <div>
              <div className="text-sm font-bold" style={{ color: "#065F46" }}>
                New outreach to make today — {approvedHooks.length} approved marketing hook{approvedHooks.length !== 1 ? "s" : ""}
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: "#047857" }}>
                Approved by marketing — use these hooks in your outreach today
              </div>
            </div>
            <a
              href="#/marketing/hooks"
              className="text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: "#047857" }}
            >
              View all
            </a>
          </div>
          <div className="divide-y divide-emerald-100 bg-white">
            {approvedHooks.slice(0, 5).map((h) => (
              <div key={h.id} className="px-4 py-3">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#ECFDF5", color: "#065F46" }}>
                    {h.hookKey.replace("_", " ")}
                  </span>
                  <span className="text-xs font-semibold" style={{ color: MI6_BLACK }}>{h.eventName}</span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: "#374151" }}>{h.text}</p>
              </div>
            ))}
            {approvedHooks.length > 5 && (
              <div className="px-4 py-2 text-xs" style={{ color: "#6B6B70" }}>
                {approvedHooks.length - 5} more approved hooks — <a href="#/marketing/hooks" style={{ color: MI6_BLACK, fontWeight: 600 }}>view all</a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Today's Queue tabs ──────────────────────────────────────── */}
      {/* SCAFFOLD: tab UI wired to cadence-store current_step as proxy. */}
      {/* Tomorrow: replace filter logic with activity_log count queries  */}
      {/* after Bryan applies the migration.                              */}
      <div
        className="flex items-center gap-0 rounded-lg overflow-hidden border border-neutral-200 self-start"
        role="tablist"
        aria-label="Queue tabs"
      >
        {([
          {
            id: "cold" as QueueTab,
            label: "Cold Cadence",
            count: coldCount,
            hint: "New prospects — step 1 only",
          },
          {
            id: "active" as QueueTab,
            label: "Active Follow-ups",
            count: followUpCount,
            hint: "Prospects past first touch",
          },
        ] as const).map((tab) => {
          const isActive = queueTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setQueueTab(tab.id)}
              title={tab.hint}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2"
              style={{
                background: isActive ? MI6_BLACK : "white",
                color: isActive ? "white" : "#6B6B70",
                borderRight: "1px solid #E5E5E7",
              }}
            >
              {tab.label}
              <span
                className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded"
                style={{
                  background: isActive ? "rgba(255,255,255,0.15)" : "#EFEFEE",
                  color: isActive ? "white" : MI6_BLACK,
                }}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Queue stats bar ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3 px-1">
        <div className="flex items-center gap-5 text-sm flex-wrap">
          <span style={{ color: MI6_BLACK }}>
            <span className="font-bold tabular-nums text-xl">{queue.total}</span>
            <span className="text-neutral-500 text-xs ml-1">actions today</span>
          </span>
          {queue.overdueCount > 0 && (
            <span>
              <span className="font-bold tabular-nums text-xl" style={{ color: MI6_RED }}>{queue.overdueCount}</span>
              <span className="text-neutral-500 text-xs ml-1">overdue</span>
            </span>
          )}
          <span className="text-neutral-500 text-xs">
            <span className="font-semibold text-neutral-600">{activeCount}</span> active ·{" "}
            <span className="font-semibold text-neutral-600">{hookCount}</span> marketing hook ·{" "}
            <span className="font-semibold text-neutral-600">{pausedCount}</span> parked
          </span>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all hover:shadow-md active:scale-95"
          style={{ background: MI6_RED }}
        >
          + Add Prospect
        </button>
      </div>

      {/* ── Queue groups (filtered by active tab) ──────────────────── */}
      {/* SCAFFOLD: filteredGroups uses current_step as proxy for activity. */}
      {/* Replace filter logic with activity_log queries post-migration.   */}
      {filteredGroups.length === 0 ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-10 text-center">
          <div className="text-neutral-500 text-sm">
            {queueTab === "cold"
              ? "No cold prospects due today. Add a prospect to start a cadence."
              : "No active follow-ups due today. Prospects appear here after their first touch."}
          </div>
          {queueTab === "cold" && (
            <button
              onClick={() => setShowAdd(true)}
              className="mt-4 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-white"
              style={{ background: MI6_RED }}
            >
              + Add Prospect
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredGroups.map((g) => (
            <StepGroup
              key={g.step}
              step={g.def}
              prospects={g.prospects}
              today={queue.today}
              onComplete={handleComplete}
              onResolve={refresh}
              doneCount={doneCounts[g.step] ?? 0}
            />
          ))}
        </div>
      )}

      {/* ── Cadence reference card ──────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
        <div
          className="px-5 pt-4 pb-2 border-b border-neutral-100"
          style={{ borderLeft: `4px solid ${MI6_BLACK}` }}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: MI6_BLACK }}>
            Cadence Program · Reference
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">
            2 emails · 2 phone calls (no VM) · 1 LinkedIn · 1 phone with acknowledgment VM · then Pursuit Decision
          </div>
        </div>
        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          {CADENCE_PROGRAM.map((s) => (
            <div key={s.id} className="flex items-start gap-2">
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0"
                style={{ background: s.id === 7 ? MI6_RED : MI6_BLACK }}
              >
                {s.id}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold" style={{ color: MI6_BLACK }}>
                  Day {s.day_offset}{s.id === 7 ? "+" : ""} · Cadence Step {s.id} · {s.label}
                </div>
                <div className="text-neutral-500 text-[11px] mt-0.5">{s.script_hint}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  // ── Embedded mode: no page chrome, just the queue card ──────────────────
  if (embedded) {
    return (
      <>
        {queueContent}
        <AddProspectModal
          open={showAdd}
          onClose={() => setShowAdd(false)}
          onAdded={refresh}
          ownerId={ownerId}
          ownerRole={role}
        />
      </>
    );
  }

  // ── Standalone page ──────────────────────────────────────────────────────
  const navRole: SecondaryNavRole = role === "ae" ? "ae" : "bdr";
  return (
    <OperatorShell breadcrumbs={[{ label: "Cadence Queue" }]} pageTitle="Cadence Queue" pageMeta="Daily cadence execution">
      {/* SecondaryNav — standardized across all role views (P0-3 fix) */}
      <SecondaryNav role={navRole} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div
          className="rounded-xl p-5 text-white"
          style={{ background: `linear-gradient(135deg, ${MI6_BLACK}, #1F2937)` }}
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">
                Mi6 Attilius · {role.toUpperCase()} · Cadence Engine
              </div>
              <h1 className="text-2xl font-bold">Today's Queue</h1>
              <div className="text-neutral-500 text-sm mt-0.5">
                Walk in · execute the queue · log auto-fills your daily activity
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-right">
                <div className="text-3xl font-bold tabular-nums">{queue.total}</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                  Actions Today
                </div>
              </div>
              {queue.overdueCount > 0 && (
                <div className="text-right">
                  <div className="text-3xl font-bold tabular-nums" style={{ color: "#FCA5A5" }}>
                    {queue.overdueCount}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                    Overdue
                  </div>
                </div>
              )}
              <button
                onClick={() => setShowAdd(true)}
                className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all hover:shadow-md active:scale-95"
                style={{ background: MI6_RED }}
              >
                + Add Prospect
              </button>
            </div>
          </div>
          <div className="mt-4 flex gap-5 text-xs flex-wrap">
            <span className="text-neutral-300">
              <span className="font-bold text-white tabular-nums">{activeCount}</span> active in cadence
            </span>
            <span className="text-neutral-300">
              <span className="font-bold text-white tabular-nums">{hookCount}</span> at marketing hook
            </span>
            <span className="text-neutral-300">
              <span className="font-bold text-white tabular-nums">{pausedCount}</span> parked
            </span>
          </div>
        </div>

        {queueContent}
      </main>

      <AddProspectModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onAdded={refresh}
        ownerId={ownerId}
        ownerRole={role}
      />
    </OperatorShell>
  );
}
